import { useRef, useEffect, useState, ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import {
  Button,
  Drawer,
  TruncatedAccount,
  Row,
  SplitButton,
} from "@narmi/design_system";
import { ContentCard, useSetChatVisibility, catcat as cc } from "cerulean";
import {
  showCancelOption,
  showReverseOption,
  useResolvedBulkPayment,
  type FormattedBulkPayment,
} from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import { useGetTemplateVersionById } from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates";
import {
  useDualApprovalPermission,
  useACHReverseCollectPermission,
  useACHReverseSendPermission,
} from "byzantine/src/dbbl/businessLogic/modules/achPayments/permissions";
import { showAmountStrikeThrough } from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import { DisplayStatus } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";
import { FILTER_CONFIG, FilterChips } from "../Filters/filterConfig";
import { PAYMENT_ACTIVITY_ROUTE } from "../routes";
import styles from "../PaymentActivity.module.scss";
import { DrawerCard, DrawerHeader } from "../common";

export type BulkPaymentDrawerProps = {
  isOpen: boolean;
  payment: FormattedBulkPayment | null;
  handleClose: () => void;
  handleToggleCancelDialog: (uuid: API.BulkPaymentId) => void;
  handleOnNext: () => void;
  handleOnPrev: () => void;
  paymentUuid: API.BulkPaymentId;
  handleReverseAllEligible: (uuid: API.BulkPaymentId) => void;
  handleSelectForReversal: (uuid: API.BulkPaymentId) => void;
};

const BulkPaymentDrawer = ({
  isOpen,
  payment,
  handleClose,
  handleToggleCancelDialog,
  handleOnNext,
  handleOnPrev,
  paymentUuid,
  handleReverseAllEligible,
  handleSelectForReversal,
}: BulkPaymentDrawerProps) => {
  const drawerIdRef = useRef(
    `Drawer-${Math.random().toString(36).slice(2, 11)}`
  );
  useSetChatVisibility(drawerIdRef.current, !isOpen);
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const features = useUserFeatures();
  const { getTemplateVersionById } = useGetTemplateVersionById();

  const { ach_payments_collect: achPaymentsCollectFeature } = features;
  const institution = useInstitutionSettings();
  const hasDualApprovalPermission = useDualApprovalPermission();
  const hasReverseSendPermission = useACHReverseSendPermission();
  const hasReverseCollectPermission = useACHReverseCollectPermission();
  const hasReversePermission =
    hasReverseSendPermission || hasReverseCollectPermission;
  const isReversalFeatureEnabled = Boolean(
    institution.features?.user_initiated_ach_reversals
  );
  // resolve from the passed payment first, fall back to a fetch for deep links
  const bulkPayment = useResolvedBulkPayment({
    bulkPayment: payment,
    bulkPaymentId: paymentUuid,
  });
  const [templateVersionLabel, setTemplateVersionLabel] = useState<
    string | null
  >(null);

  useEffect(() => {
    let shouldUpdateState = true;

    if (!bulkPayment?.templateVersion) {
      setTemplateVersionLabel(null);
    } else {
      setTemplateVersionLabel(null);

      getTemplateVersionById(bulkPayment.templateVersion, {
        onData: (version) => {
          if (!shouldUpdateState) return;

          const templateGroupName =
            version.group_name ?? version.template_group_name;
          const versionNumber = version.version_number;

          if (!templateGroupName || versionNumber == null) {
            setTemplateVersionLabel(null);
            return;
          }

          setTemplateVersionLabel(
            l10n.getString("bulk-template-version-label", {
              templateName: templateGroupName,
              versionNumber,
            })
          );
        },
        onError: () => {
          if (!shouldUpdateState) return;
          setTemplateVersionLabel(null);
        },
      });
    }

    return () => {
      shouldUpdateState = false;
    };
  }, [bulkPayment?.templateVersion, getTemplateVersionById, l10n]);

  if (!bulkPayment) {
    return null;
  }

  const {
    description,
    entryDesc,
    paymentCount,
    paymentCreditCount,
    paymentDebitCount,
    netTotalDollarsWithSign,
    creditTotalDollars,
    debitTotalDollars,
    secCode,
    title,
    institutionAccount,
    displayStatus,
    groupPosting,
    dualApprovalId,
    companyId,
    companyName,
    longDateRange,
    isExpedited,
    organizationFile,
  } = bulkPayment;

  const showStrikeThrough = showAmountStrikeThrough(displayStatus);
  const showCancelButton = showCancelOption(bulkPayment);
  const isPendingApproval = displayStatus === DisplayStatus.pending_approval;
  const showReverseButtons = showReverseOption(bulkPayment, {
    isReversalFeatureEnabled,
    hasReversePermission,
  });

  const handleDownloadFile = async () => {
    if (!organizationFile) return;
    try {
      const encodedFileName = organizationFile.file_name
        .split("/")
        .map(encodeURIComponent)
        .join("/");
      const endpoint =
        organizationFile.file_type === "nacha"
          ? `ach_import/nacha/download/${encodedFileName}`
          : `ach_import/csv/download/${encodedFileName}`;

      const response = await fetch(`/v1/${endpoint}`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to download file");
      }

      const blob = await response.blob();
      const displayName = organizationFile.file_name.includes("/")
        ? organizationFile.file_name.split("/").pop() ??
          organizationFile.file_name
        : organizationFile.file_name;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = displayName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const TopCard = () => {
    const getTopCardDetails = () => (
      <>
        {paymentCreditCount > 0 ? (
          <>
            <h5 className={styles.drawerLabelHeading}>
              {l10n.getString("label-sending")}
            </h5>
            <div className="margin--bottom--m">
              {creditTotalDollars}{" "}
              {l10n.getString("via-payments-cta", {
                total: paymentCreditCount,
              })}
            </div>
          </>
        ) : null}

        {achPaymentsCollectFeature && paymentDebitCount > 0 ? (
          <>
            <h5 className={styles.drawerLabelHeading}>
              {l10n.getString("label-collecting")}
            </h5>
            <div className="margin--bottom--m">
              {debitTotalDollars}{" "}
              {l10n.getString("via-payments-cta", {
                total: paymentDebitCount,
              })}
            </div>
          </>
        ) : null}

        {longDateRange ? (
          <>
            <h5 className={styles.drawerLabelHeading}>
              {l10n.getString("label-scheduled-date")}
            </h5>
            <div className="margin--bottom--m">
              {longDateRange}
              {isExpedited && (
                <span className="margin--left--xs narmi-icon-zap" />
              )}
            </div>
          </>
        ) : null}

        {templateVersionLabel ? (
          <>
            <h5 className={styles.drawerLabelHeading}>
              {l10n.getString("label-template-used")}
            </h5>
            <div className="margin--bottom--m">{templateVersionLabel}</div>
          </>
        ) : null}
      </>
    );

    const handleClickViewApprovals = () => {
      window.location.assign(
        `/approvals/pending?dualApprovalId=${dualApprovalId}`
      );
    };

    const handleClickViewPayments = () => {
      navigate({
        pathname: `/${PAYMENT_ACTIVITY_ROUTE}`,
        search: `?${FILTER_CONFIG[FilterChips.BULK_PAYMENT].queryKey}=${
          bulkPayment?.uuid
        }`,
      });
    };

    return (
      <div className="margin--top--l">
        <ContentCard kind="bordered">
          <h1 className="fontColor--primary">
            <span className={cc({ [styles.strikeThrough]: showStrikeThrough })}>
              {netTotalDollarsWithSign}
            </span>
          </h1>
          <hr className="margin--bottom--l margin--top--l" />
          {getTopCardDetails()}
          {paymentCount && !isPendingApproval ? (
            <>
              <h5 className={cc("margin--none", styles.drawerLabelHeading)}>
                {l10n.getString("label-bulk-details")}
              </h5>
              <Button
                kind="plain"
                size="s"
                onClick={handleClickViewPayments}
                testId="view-bulk-payments"
              >
                {l10n.getString("view-payments-cta", {
                  total: paymentCount,
                })}
                <span className="narmi-icon-chevron-right" />
              </Button>
            </>
          ) : null}
          {isPendingApproval && dualApprovalId && hasDualApprovalPermission ? (
            <div>
              <Button
                kind="plain"
                size="s"
                onClick={handleClickViewApprovals}
                testId="view-bulk-approvals"
              >
                {l10n.getString("label-view-approvals")}
                <span className="narmi-icon-chevron-right" />
              </Button>
            </div>
          ) : null}
          {groupPosting ? (
            <div className="fontSize--xs fontColor--primary">
              {l10n.getString("group-posting-copy")}
            </div>
          ) : null}
          {features.ach_file_import && organizationFile ? (
            <div className="margin--top--m">
              <h5 className={cc("margin--none", styles.drawerLabelHeading)}>
                {l10n.getString("label-file")}
              </h5>
              <Button
                kind="plain"
                size="s"
                onClick={handleDownloadFile}
                testId="download-organization-file"
              >
                <span className="narmi-icon-download margin--right--xs" />
                {l10n.getString("label-download-file")}
                <span className="narmi-icon-chevron-right" />
              </Button>
            </div>
          ) : null}
        </ContentCard>
      </div>
    );
  };

  const PaymentDetailsCard = () => {
    const rows: Array<[string, string | undefined | ReactElement<any>]> = [];

    const truncatedAccountEl = (
      <TruncatedAccount
        name={institutionAccount?.name ?? ""}
        lastFour={institutionAccount?.getMaskedNumber() ?? ""}
      />
    );

    rows.push(
      [l10n.getString("label-account"), truncatedAccountEl],
      [l10n.getString("label-company-name"), companyName],
      [l10n.getString("label-company-id"), companyId],
      [l10n.getString("label-payment-type"), secCode],
      [l10n.getString("label-purpose-payment"), entryDesc]
    );

    if (description) {
      rows.push([l10n.getString("label-note-internal"), description]);
    }

    return (
      <DrawerCard
        rows={rows}
        title={l10n.getString("ach-payment-payment-details-title")}
      />
    );
  };

  const renderDrawerContent = () => (
    <>
      <DrawerHeader title={title} status={displayStatus} />
      <TopCard />
      <PaymentDetailsCard />
    </>
  );

  return (
    <Drawer
      depth="524px"
      isOpen={isOpen}
      onUserDismiss={handleClose}
      paddingSize="xl"
      onNext={handleOnNext}
      onPrev={handleOnPrev}
      {...((showCancelButton || showReverseButtons) && {
        footer: (
          <Row justifyContent="end">
            {showReverseButtons && (
              <Row.Item shrink>
                <SplitButton
                  kind="primary"
                  label={l10n.getString("label-reverse-payments")}
                >
                  <SplitButton.Menu>
                    <SplitButton.MenuItem
                      startIcon={null}
                      endIcon={null}
                      label={l10n.getString("label-reverse-all-eligible")}
                      onSelect={() => {
                        handleReverseAllEligible(bulkPayment.uuid);
                      }}
                    />
                    <SplitButton.MenuItem
                      startIcon={null}
                      endIcon={null}
                      label={l10n.getString("label-select-for-reversal")}
                      onSelect={() => {
                        handleSelectForReversal(bulkPayment.uuid);
                      }}
                    />
                  </SplitButton.Menu>
                </SplitButton>
              </Row.Item>
            )}
            {showCancelButton && (
              <Row.Item shrink>
                <Button
                  kind="primary"
                  onClick={() =>
                    handleToggleCancelDialog(
                      bulkPayment?.uuid as API.BulkPaymentId
                    )
                  }
                  label={l10n.getString("label-cancel-payment")}
                  testId="cancel-bulk-payment-button"
                />
              </Row.Item>
            )}
          </Row>
        ),
      })}
    >
      {renderDrawerContent()}
    </Drawer>
  );
};

export default BulkPaymentDrawer;
